// LIBRERIE/IMPORTS
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from "react-i18next";

// COMPONENTI FIGLI
import { NavigationTabs } from './components/NavigationTabs';
import { MainSlider } from './components/MainSlider';
import { ThumbnailSlider } from './components/ThumbnailSlider';
import { GalleryModal } from './components/GalleryModal';

// TIPI E HOOKS
import { ImageDetail } from './types';
import useValidImages from '../../../hooks/useValidImages';

interface PropertyGalleryProps {
    images: ImageDetail[];
    caption: string;
    hasPlanimetry: boolean;
}

export const PropertyGallery: React.FC<PropertyGalleryProps> = ({
    images,
    caption,
    hasPlanimetry
}) => {
    const { t } = useTranslation()
    const planimetryTabTextForNav = t('gallery.navigationTabPlanimetry');
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const validImages = useValidImages(images);

    // Memoization delle funzioni
    const scrollToPlanimetry = useCallback(() => {
        const element = document.getElementById('planimetrie');
        element?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    const openModal = useCallback((index: number) => {
        setCurrentImageIndex(index);
        setModalIsOpen(true);
    }, []);

    const closeModal = useCallback(() => setModalIsOpen(false), []);

    const nextImage = useCallback(() => {
        if (validImages.length > 0) {
            setCurrentImageIndex((prev) => (prev + 1) % validImages.length);
        }
    }, [validImages.length]);

    const prevImage = useCallback(() => {
        if (validImages.length > 0) {
            setCurrentImageIndex((prev) => (prev - 1 + validImages.length) % validImages.length);
        }
    }, [validImages.length]);

    useEffect(() => { setCurrentImageIndex(0); }, [validImages]);

    const modalCloseLabel = t('gallery.modalCloseButtonLabel');
    const modalPrevLabel = t('gallery.modalPrevButtonLabel');
    const modalNextLabel = t('gallery.modalNextButtonLabel');
    const modalContentLabelText = t('gallery.modalContentLabel');
    const modalCounterPatternText = t('property.modalCounterPattern');

    return (
        <>
            {hasPlanimetry && (
                <NavigationTabs scrollToPlanimetry={scrollToPlanimetry} planimetryTabText={planimetryTabTextForNav}/>
            )}

            {validImages.length > 0 && ( // Mostra slider solo se ci sono immagini
                <div className='slider-slick'>
                    <MainSlider
                        images={validImages}
                        gallerySettings={{
                            dots: false,
                            infinite: true,
                            speed: 500,
                            slidesToShow: 1,
                            slidesToScroll: 1,
                        }}
                        activeIndex={0}
                        setActiveIndex={setCurrentImageIndex}
                        onImageClick={openModal}
                    />

                    <ThumbnailSlider
                        images={validImages}
                        activeIndex={currentImageIndex}
                        setActiveIndex={setCurrentImageIndex}
                        onImageClick={openModal}
                    />
                </div>
            )}

            {validImages.length > 0 && ( // Mostra la modale solo se ci sono immagini valide
                <GalleryModal
                    isOpen={modalIsOpen}
                    onClose={closeModal}
                    currentImage={validImages[currentImageIndex] || validImages[0]}
                    currentIndex={currentImageIndex}
                    totalImages={validImages.length}
                    onPrev={prevImage}
                    onNext={nextImage}
                    caption={caption}
                    closeButtonLabel={modalCloseLabel}
                    prevButtonLabel={modalPrevLabel}
                    nextButtonLabel={modalNextLabel}
                    counterPattern={modalCounterPatternText}
                    contentLabel={modalContentLabelText}
                />
            )}
        </>
    );
};

export default PropertyGallery;